import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #0d9488;
  font-style: italic;
`;

const FeatureDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: rgba(13, 148, 136, 0.3);
  margin: 4rem 0;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const PrivateLessons: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const features = [
    {
      title: language === 'it' ? 'PERSONALIZZAZIONE E FLESSIBILITÀ' : 'CUSTOMIZATION AND FLEXIBILITY',
      description: language === 'it' 
        ? 'Le lezioni private sono progettate su misura per le tue esigenze e il tuo livello di esperienza. Che tu sia un principiante che cerca di iniziare con il piede giusto o un host esperto che vuole perfezionare le proprie strategie, le lezioni saranno adattate alle tue specifiche necessità.'
        : 'Private lessons are custom designed for your needs and experience level. Whether you\'re a beginner looking to get started on the right foot or an experienced host looking to perfect your strategies, lessons will be tailored to your specific needs.'
    },
    {
      title: language === 'it' ? 'COMPRENSIONE DEI FONDAMENTALI' : 'UNDERSTANDING THE FUNDAMENTALS',
      description: language === 'it' 
        ? 'Imparerai i fondamenti degli affitti brevi, incluse le leggi e i regolamenti locali, la creazione di un annuncio coinvolgente, le strategie di prezzo e come gestire le prenotazioni e i pagamenti.'
        : 'You will learn the fundamentals of short-term rentals, including local laws and regulations, creating an engaging listing, pricing strategies, and how to manage reservations and payments.'
    },
    {
      title: language === 'it' ? 'OTTIMIZZAZIONE DEGLI ANNUNCI ONLINE' : 'OPTIMIZING ONLINE ADVERTISEMENTS',
      description: language === 'it' 
        ? 'Ti guiderò attraverso i passaggi per creare un annuncio coinvolgente e attraente su piattaforme come Airbnb, Booking.com o Vrbo. Imparerai a scrivere descrizioni efficaci, selezionare foto accattivanti e gestire le recensioni degli ospiti.'
        : 'I\'ll walk you through the steps to create an engaging and attractive listing on platforms like Airbnb, Booking.com or Vrbo. You will learn to write effective descriptions, select captivating photos and manage guest reviews.'
    },
    {
      title: language === 'it' ? 'STRATEGIE DI PREZZO DINAMICHE' : 'DYNAMIC PRICING STRATEGIES',
      description: language === 'it' 
        ? 'Comprenderai come impostare tariffe competitive e utilizzare strategie di prezzo dinamiche per massimizzare i guadagni in base alla domanda e all\'offerta del mercato.'
        : 'You will understand how to set competitive rates and use dynamic pricing strategies to maximize earnings based on market supply and demand.'
    },
    {
      title: language === 'it' ? 'GESTIONE DELLE PRENOTAZIONI E COMUNICAZIONE' : 'RESERVATION AND COMMUNICATION MANAGEMENT',
      description: language === 'it' 
        ? 'Ti insegnerò le migliori pratiche per gestire le prenotazioni, organizzare check-in e check-out senza problemi e mantenere una comunicazione efficace con gli ospiti per garantire esperienze positive.'
        : 'I will teach you best practices for managing reservations, arranging hassle-free check-in and check-out, and maintaining effective communication with guests to ensure positive experiences.'
    },
    {
      title: language === 'it' ? 'RISOLVERE PROBLEMI E MIGLIORARE LE RECENSIONI' : 'RESOLVE PROBLEMS AND IMPROVE REVIEWS',
      description: language === 'it' 
        ? 'Imparerai a gestire situazioni difficili come ospiti insoddisfatti o possibili danni alla proprietà. Ti darò strategie per affrontare le recensioni negative e migliorare la tua reputazione online.'
        : 'You will learn to handle difficult situations such as dissatisfied guests or possible damage to the property. I\'ll give you strategies to deal with negative reviews and improve your online reputation.'
    },
    {
      title: language === 'it' ? 'OTTIMIZZAZIONE DELLE OPERAZIONI' : 'OPTIMIZING OPERATIONS',
      description: language === 'it' 
        ? 'Ti guiderò attraverso strategie per ottimizzare le operazioni quotidiane, inclusi pulizia, manutenzione e gestione delle emergenze per garantire una gestione efficiente e fluida della tua proprietà.'
        : 'I will guide you through strategies to optimize daily operations, including cleaning, maintenance and emergency management to ensure efficient and smooth management of your property.'
    },
    {
      title: language === 'it' ? 'MIGLIORAMENTO CONTINUO' : 'CONTINUOUS IMPROVEMENT',
      description: language === 'it' 
        ? 'Le lezioni private non si limitano a un solo incontro. Sarò disponibile per fornire supporto continuo, rispondere alle tue domande e offrire suggerimenti mentre continui il tuo percorso negli affitti brevi.'
        : 'Private lessons are not limited to just one meeting. I will be available to provide ongoing support, answer your questions and offer suggestions as you continue your short-term rental journey.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Lezioni Private | J-O-H' : 'Private Lessons | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Lezioni private personalizzate per apprendere la gestione professionale degli affitti brevi e massimizzare i tuoi profitti' 
            : 'Personalized private lessons to learn professional management of short-term rentals and maximize your profits'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/training">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'LEZIONI PRIVATE' : 'PRIVATE LESSONS'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Le lezioni private sugli affitti brevi offrono un\'opportunità personalizzata per imparare tutto ciò che devi sapere sul mondo degli affitti a breve termine, dalle basi ai dettagli più complessi. Queste lezioni sono ideali per host, proprietari di case vacanze o chiunque sia interessato a sfruttare il potenziale del mercato degli affitti brevi.'
              : 'Private short-term rental lessons offer a personalized opportunity to learn everything you need to know about the world of short-term rentals, from the basics to the most complex details. These lessons are ideal for hosts, vacation rental owners, or anyone interested in tapping into the potential of the short-term rental market.'}
          </Description>
          
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
          
          <Divider />
          
          <SectionTitle>
            {language === 'it' ? 'PERCHÉ SCEGLIERE LE LEZIONI PRIVATE?' : 'WHY CHOOSE PRIVATE LESSONS?'}
          </SectionTitle>
          
          <Description>
            {language === 'it' 
              ? 'Le lezioni private offrono un approccio mirato e personalizzato che ti permette di concentrarti esattamente sulle aree in cui hai bisogno di miglioramento o approfondimento. Con un\'attenzione individuale e un piano formativo su misura, potrai progredire più rapidamente e affrontare specifiche sfide relative alla tua proprietà o al tuo contesto di mercato. Il nostro approccio flessibile ti consente di imparare al tuo ritmo, fare tutte le domande necessarie e ricevere feedback immediato per ottimizzare la tua gestione degli affitti brevi.'
              : 'Private lessons offer a targeted and personalized approach that allows you to focus exactly on the areas where you need improvement or deepening. With individual attention and a tailor-made training plan, you will be able to progress more quickly and address specific challenges related to your property or market context. Our flexible approach allows you to learn at your own pace, ask all the necessary questions and receive immediate feedback to optimize your short-term rental management.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per una Consulenza' : 'Contact Us for a Consultation'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default PrivateLessons; 